// src/utils/tenantConfig.js
import axios from "axios";
import { LRUCache } from "lru-cache";

// cache up to 50 tenants, expire entries after 1h
const cache = new LRUCache({ max: 50, ttl: 1000 * 60 * 5 });
// up to 50 tenants, cache for 5 minutes per session
/**
 * Fetches the tenant's config JSON from our API.
 * Caches it in-memory for the browsing session.
 *
 * @param {string} tenantId
 * @returns {Promise<Record<string, any>>}
 */
export async function loadTenantConfig(tenantId) {
  console.log(`[loadTenantConfig] loading config for tenant: "${tenantId}"`);

  // 1) Return cached if present
  if (cache.has(tenantId)) {
    console.log(`[loadTenantConfig] cache HIT for "${tenantId}"`);
    return cache.get(tenantId);
  }

  // 2) otherwise fetch
  const url = `${window.location.protocol}//${window.location.host}/api/configurationmanagement/tenants/${tenantId}/config`;
  console.log(`[loadTenantConfig] fetching from ${url}`);
  const resp = await axios.get(url, {
    headers: { "X-Tenant-ID": tenantId },
  });
  const cfg = resp.data;
  console.log(`[loadTenantConfig] fetched config:`, cfg);

  // 3) cache and return
  cache.set(tenantId, cfg);
  return cfg;
}

/**
 * Applies tenant configuration to CSS custom properties
 * This is the SHARED logic that was duplicated across multiple App.js files
 */
export function applyTenantConfigToCSS(cfg) {
  // Map tenant config keys to CSS custom properties
  const cssVariableMapping = {
    COLOR_PRIMARY: "--play-button-color",
    COLOR_SECONDARY: "--play-button-playing-color",
    BACKGROUND_COLOR: "--player-background-color",
    STATION_NAME: "--station-name",
    // Additional theming options for dramatic differences
    HEADER_BACKGROUND: "--header-background-color",
    TEXT_COLOR: "--primary-text-color",
    ACCENT_COLOR: "--accent-color",
    // Logo configuration
    LOGO_URL: "--logo-url",
    // Background images
    BACKGROUND_IMAGE_PORTRAIT: "--background-image-portrait",
    BACKGROUND_IMAGE_LANDSCAPE: "--background-image-landscape",
  };

  // inject CSS variables based on mapping + any keys that already start with "--"
  Object.entries(cfg).forEach(([key, value]) => {
    if (key.startsWith("--")) {
      // Direct CSS variable
      document.documentElement.style.setProperty(key, value);
      console.log(`[TenantConfig] injected CSS var ${key}: ${value}`);
    } else if (cssVariableMapping[key]) {
      // Mapped config key
      const cssVar = cssVariableMapping[key];
      document.documentElement.style.setProperty(cssVar, value);
      console.log(`[TenantConfig] mapped ${key} -> ${cssVar}: ${value}`);
    }
  });
}